// BFSG
import * as React from 'react'
import styled from 'styled-components'
import Link from 'theme/atoms/Link'
import useLazyImgSrc from 'hooks/useLazyImgSrc'
import theme from 'theme'

import { OptImg } from 'utils/imageOptimization'
import LinkArrow from 'assets/link-arrow.svg'

export type Props = {
  img: OptImg
  imageLink?: string
  label?: string
  imageCaption?: string
  alt: string
  fill?: boolean
  style?: string
}

export default function Image(props: Props) {
  const { img, imageLink, label, imageCaption, style } = props

  const [ref, imgUrl] = useLazyImgSrc<HTMLImageElement>(img, 100, img.base64)
  const ratio =
    typeof img === 'object' ? img.sizes.w / (img.sizes.h || 1) : null

  if (!style || style === 'varianta')
    return (
      <Wrapper className="Image" data-cy-ctx="molecules/Image">
        <ImageWrapper to={imageLink} className={`image-wrapper`} $ratio={ratio}>
          <ActualImage src={imgUrl} ref={ref} alt={props.alt} />
        </ImageWrapper>

        {imageCaption && (
          <figcaption data-cy-state="has-caption" className="caption">
            {imageCaption}
          </figcaption>
        )}
        {label && (
          <figcaption className="label" data-cy-state="has-label">
            <span>{label}</span>
          </figcaption>
        )}
      </Wrapper>
    )

  return (
    <Wrapper className="Image" data-cy-ctx="molecules/Image">
      <ImageWrapper to={imageLink} className={`image-wrapper`} $ratio={ratio}>
        <ActualImage src={imgUrl} ref={ref} alt={props.alt} />
        <div className="indicator">
          <span>
            {label} <LinkArrow />
          </span>
        </div>
      </ImageWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.figure`
  width: 100%;
  margin: 0;
  position: relative;
  text-align: center;

  > img,
  .image-wrapper > img {
    width: 100%;
  }
  > .image-wrapper {
    width: 100%;
  }
  .caption {
    padding-top: ${theme.spacing('xxs')};
    margin: 0;
    margin-bottom: ${theme.spacing('s')};
    text-align: left;
    color: ${theme.colors.shade.b3};
    ${theme.ty('r-s')}

    @media (min-width: ${theme.ms.MD}px) {
      margin-bottom: 0;
    }
  }
  .label {
    margin: 0;
    margin-top: ${theme.spacing('xs')};
    height: 24px;
    ${theme.ty('rc-l')}
    position: relative;
    width: 100%;

    @media (min-width: ${theme.ms.LG}px) {
      height: 28px;
      ${theme.ty('rc-xl')}
    }

    > span {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`

const ImageWrapper = styled(Link)<{ $ratio: number | null }>`
  box-sizing: border-box;
  position: relative;
  display: inline-block;
  width: 100%;
  overflow: hidden;
  height: auto;

  > img {
    width: 100%;
  }
  > .indicator {
    position: absolute;
    bottom: ${theme.spacing('xs')};
    left: ${theme.spacing('xs')};
    margin-right: ${theme.spacing('m')};
    padding: ${theme.spacing('xxs')} ${theme.spacing('s')};
    border-radius: 15px 15px 15px 0;
    background: ${theme.colors.white};

    @media (min-width: ${theme.ms.MD}px) {
      left: ${theme.spacing('m')};
      bottom: ${theme.spacing('m')};
      max-width: 41.25rem;
      padding: ${theme.spacing('xs')} ${theme.spacing('s')};
    }

    > span {
      ${theme.ty('rc-base')}
      color:${theme.colors.b0};
      ${theme.ty('rc-s', '700')}

      @media (min-width: ${theme.ms.MD}px) {
        display: block;
        ${theme.ty('rc-base', 'bold')}
      }
      > svg {
        margin-bottom: -1px;
        color: ${theme.colors.b0};
        width: 12px;
        margin-left: 10px;
        g {
          color: ${theme.colors.b0};
          stroke: ${theme.colors.b0};
          filter: none;
        }
      }
    }
  }
`

const ActualImage = styled.img`
  padding: 0;
  margin: 0;
`
